import { inject, ref } from 'vue'
import { useVueI18n } from '../locales'
import { RouteLocationNormalizedLoaded, useRoute } from 'vue-router'
import { TemplateContentLibraryService } from '../services'
import { ContentLibraryCategoryEnum, UpdateDocument } from '../types'
import { useNotify } from './useNotify'

export const useContentLibraryFactory = (
  routeData?: RouteLocationNormalizedLoaded
) => {
  const { handleError, handleInfo } = useNotify()
  const contentLibraryLoading = ref(false)
  const fetchContentLibraryLoading = ref(false)
  const fetchFeaturedContentLibraryLoading = ref(false)
  const contentLibrary = ref([])
  const featuredContentLibrary = ref([])
  const totalFeaturedDocuments = ref(0)
  const totalDocuments = ref(0)
  const error = ref(null)
  const contentLibraryTemplate = ref(null)
  const pageNum = ref(0)
  const pageLimit = ref(20)
  const pageNumFeatured = ref(0)
  const clFeaturedData = ref<Array<any>>([])
  const clData = ref<Array<any>>([])
  const isAllClDataLoaded = ref(false)
  const isAllFeaturedClDataLoaded = ref(false)
  const clDataIdMap = ref({})
  const t = useVueI18n()
  const addToContentLibrary = async (params: UpdateDocument) => {
    try {
      contentLibraryLoading.value = true
      const { data } = await TemplateContentLibraryService.addToContentLibrary({
        ...params,
      })
      contentLibraryTemplate.value = data
      handleInfo(t('common.contentSavedSuccessfully'))
      return data
    } catch (err) {
      console.error(err)
      error.value = err
      handleError(err)
    } finally {
      contentLibraryLoading.value = false
    }
  }

  async function getFeaturedContentLibrary({
    offset,
    limit,
    name,
  }: {
    offset: number
    limit: number
    name?: string
  }) {
    try {
      fetchFeaturedContentLibraryLoading.value = true
      let route = useRoute()
      if (!route) {
        route = inject<RouteLocationNormalizedLoaded>(
          'route'
        ) as RouteLocationNormalizedLoaded
      }
      const locationId = (routeData?.params?.locationId ||
        routeData?.params?.location_id ||
        route?.params?.locationId ||
        route?.params?.location_id) as string

      const payload = {
        locationId,
        skip: offset,
        limit: limit,
        ...(name ? { name } : {}),
        contentLibraryCategory: ContentLibraryCategoryEnum.featured,
      }

      const { data } =
        await TemplateContentLibraryService.fetchRecentContentFromContentLibrary(
          payload
        )
      featuredContentLibrary.value = data.data
      totalFeaturedDocuments.value = data.total
      return data
    } catch (err) {
      console.error(err)
      error.value = err
      handleError(err)
      return []
    } finally {
      fetchFeaturedContentLibraryLoading.value = false
    }
  }

  async function getCustomContentLibrary({
    offset,
    limit,
    name,
  }: {
    offset: number
    limit: number
    name?: string
  }) {
    try {
      fetchContentLibraryLoading.value = true
      let route = useRoute()
      if (!route) {
        route = inject<RouteLocationNormalizedLoaded>(
          'route'
        ) as RouteLocationNormalizedLoaded
      }
      const locationId = (routeData?.params?.locationId ||
        routeData?.params?.location_id ||
        route?.params?.locationId ||
        route?.params?.location_id) as string

      const payload = {
        locationId,
        skip: offset,
        limit: limit,
        ...(name ? { name } : {}),
        contentLibraryCategory: ContentLibraryCategoryEnum.custom,
      }

      const { data } =
        await TemplateContentLibraryService.fetchRecentContentFromContentLibrary(
          payload
        )
      contentLibrary.value = data.data
      totalDocuments.value = data.total
      return data
    } catch (err) {
      console.error(err)
      error.value = err
      handleError(err)
      return []
    } finally {
      fetchContentLibraryLoading.value = false
    }
  }

  const getCustomContentLibPaginated = async (
    searchName = '',
    isPageInc = false,
    isPageReset = false
  ) => {
    if (isPageReset) {
      pageNum.value = 0
      clData.value = []
      clDataIdMap.value = {}
    }
    if (isPageInc) {
      pageNum.value += 1
    }
    if (!fetchContentLibraryLoading.value) {
      const response = await getCustomContentLibrary({
        limit: pageLimit.value,
        offset: pageNum.value,
        ...(searchName && { name: searchName }),
      })
      if (response?.data.length > 0) {
        const idMap = response.data.reduce((acc: any, curr: any) => {
          acc[curr._id || curr.id] = curr.pages
          return acc
        }, {})
        clDataIdMap.value = { ...clDataIdMap.value, ...idMap }
        clData.value = clData.value.concat(response.data)
      } else if (response?.data.length === 0) {
        isAllClDataLoaded.value = true
        return
      }
    }
  }

  const getFeaturedContentLibPaginated = async (
    searchName = '',
    isPageInc = false,
    isPageReset = false
  ) => {
    if (isPageReset) {
      pageNumFeatured.value = 0
      clFeaturedData.value = []
    }
    if (isPageInc) {
      pageNumFeatured.value += 1
    }
    if (!fetchContentLibraryLoading.value) {
      const response = await getFeaturedContentLibrary({
        limit: pageLimit.value,
        offset: pageNumFeatured.value,
        ...(searchName && { name: searchName }),
      })
      if (response?.data.length > 0) {
        clFeaturedData.value = clFeaturedData.value.concat(response.data)
      } else if (response?.data.length === 0) {
        isAllFeaturedClDataLoaded.value = true
        return
      }
    }
  }

  return {
    contentLibraryLoading,
    fetchContentLibraryLoading,
    error,
    addToContentLibrary,
    getCustomContentLibrary,
    getCustomContentLibPaginated,
    contentLibrary,
    totalDocuments,
    clData,
    isAllClDataLoaded,
    clDataIdMap,
    getFeaturedContentLibPaginated,
    fetchFeaturedContentLibraryLoading,
    clFeaturedData,
    totalFeaturedDocuments,
  }
}
